import React, { Component } from 'react';
import DtnService from '../../../../services/DtnService';

export default class StatusButtons extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loadingCompleted: false,

            showReasonModal: false,
            showManagerReviewModal: false,
            showSuperadminReviewModal: false,

            reason: '',
            managerReviewReason: '',

            pendingStatus: null,
            requiresDtnReasonForReview: false,
            requiresDtnReasonForSuperadmin: false,
            hasPendingSuperadminReview: false,
        };

        this.dtnService = new DtnService();
    }

    async componentDidMount() {
        await this.checkPendingSuperadminReview();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.jobId !== this.props.jobId || prevProps.job?.status !== this.props.job?.status) {
            await this.checkPendingSuperadminReview();
        }
    }

    checkPendingSuperadminReview = async () => {
        try {
            const superadminRes = await this.dtnService.requiresSuperadminReview(this.props.jobId);

            this.setState({
                hasPendingSuperadminReview: !!superadminRes.data.hasPending,
            });
        } catch (err) {
            console.error('Error checking superadmin review status:', err);
        }
    };

    changeStatus = async (statusObj) => {
        try {
            this.props.disableStatusButtons?.();

            if (this.props.saveDtn) {
                const dtnSaved = await this.props.saveDtn(false);

                if (!dtnSaved) {
                    toastr.error('Please fix validation errors and save the DTN before updating the job status.');
                    this.props.enableStatusButtons?.();
                    return;
                }
            }

            if (statusObj.waste_journey) {
                this.submitStatus(statusObj);
                return;
            }

            const roleName = this.props.user.role?.name;
            const isAdminOrManager = ['admin', 'manager'].includes(roleName);
            const isSuperadmin = ['superadmin', 'admin'].includes(roleName);

            if (['completed', 'done'].includes(statusObj.status) && isSuperadmin) {
                const superadminRes = await this.dtnService.requiresSuperadminReview(this.props.jobId);

                if (superadminRes.data.hasPending) {
                    const requiresWtnReason = !!superadminRes.data.requiresWtnReason;

                    if (requiresWtnReason) {
                        this.setState({
                            showSuperadminReviewModal: true,
                            pendingStatus: statusObj,
                            requiresDtnReasonForSuperadmin: true,
                            hasPendingSuperadminReview: true,
                        });

                        this.props.enableStatusButtons?.();
                        return;
                    }

                    this.submitStatus(
                        {
                            ...statusObj,
                            superadmin_review_approve: 1,
                        },
                        { source: 'superadmin_review' }
                    );

                    return;
                }
            }

            const hasPendingManagerReview =
                Number(this.props.job.manager_review) === 1 ||
                this.props.job.reviews?.some(
                    (review) =>
                        review.type === 'manager' &&
                        review.review_category === 'approval' &&
                        Number(review.approved) === 0
                );

            if (statusObj.status === 'completed' && isAdminOrManager && hasPendingManagerReview) {
                const reasonRes = await this.dtnService.requiresDtnReason(this.props.jobId);

                this.setState({
                    showManagerReviewModal: true,
                    pendingStatus: statusObj,
                    requiresDtnReasonForReview: !!reasonRes.data.requiresReason,
                });

                this.props.enableStatusButtons?.();
                return;
            }

            const reasonRes = await this.dtnService.requiresDtnReason(this.props.jobId);

            if (reasonRes.data.requiresReason && !statusObj.reason) {
                this.setState({
                    showReasonModal: true,
                    pendingStatus: statusObj,
                    requiresDtnReasonForReview: false,
                    requiresDtnReasonForSuperadmin: false,
                });

                this.props.enableStatusButtons?.();
                return;
            }

            this.submitStatus(statusObj);
        } catch (err) {
            console.error('Error changing status:', err);
            toastr.error('An error occurred while trying to change the job status.');
            this.props.enableStatusButtons?.();
        }
    };

    submitStatusWithReason = () => {
        const { pendingStatus, reason } = this.state;

        if (!reason.trim()) {
            toastr.error('Please enter a DTN reason.');
            return;
        }

        const payload = {
            ...pendingStatus,
            reason: reason.trim(),
        };

        this.setState(
            {
                reason: '',
                showReasonModal: false,
                pendingStatus: payload,
            },
            () => {
                this.changeStatus(payload);
            }
        );
    };

    submitManagerReview = () => {
        const { pendingStatus, managerReviewReason, reason, requiresDtnReasonForReview } = this.state;

        if (!managerReviewReason?.trim()) {
            toastr.error('Please enter a manager review reason.');
            return;
        }

        if (requiresDtnReasonForReview && !reason?.trim()) {
            toastr.error('Please enter a DTN reason.');
            return;
        }

        const payload = {
            ...pendingStatus,
            manager_review_reason: managerReviewReason.trim(),
            manager_review_approve: 1,
        };

        if (requiresDtnReasonForReview) {
            payload.reason = reason.trim();
        }

        this.submitStatus(payload, { source: 'manager_review' });
    };

    submitSuperadminReview = () => {
        const { pendingStatus, reason, requiresDtnReasonForSuperadmin } = this.state;

        if (requiresDtnReasonForSuperadmin && !reason?.trim()) {
            toastr.error('Please enter a DTN reason.');
            return;
        }

        const payload = {
            ...pendingStatus,
            superadmin_review_approve: 1,
        };

        if (requiresDtnReasonForSuperadmin) {
            payload.reason = reason.trim();
        }

        this.submitStatus(payload, { source: 'superadmin_review' });
    };

    submitStatus = (statusObj, options = {}) => {
        const { source = null } = options;
        this.props.disableStatusButtons?.();

        const formData = new FormData();

        formData.append('_method', 'PATCH');
        formData.append('status', statusObj.status);

        if (statusObj.reason) {
            formData.append('reason', statusObj.reason);
        }

        if (statusObj.waste_journey !== undefined) {
            formData.append('waste_journey', statusObj.waste_journey);
        }

        if (statusObj.manager_review_reason) {
            formData.append('manager_review_reason', statusObj.manager_review_reason);
        }

        if (statusObj.manager_review_approve) {
            formData.append('manager_review_approve', statusObj.manager_review_approve);
        }

        if (statusObj.superadmin_review_approve) {
            formData.append('superadmin_review_approve', statusObj.superadmin_review_approve);
        }

        this.setState({ loadingCompleted: true });

        this.dtnService
            .updateJobStatus(this.props.jobId, formData)
            .then((res) => {
                const needsSuperadminReview = !!res.data?.needs_superadmin_review;

                if (source === 'manager_review' && needsSuperadminReview) {
                    this.dtnService.requiresSuperadminReview(this.props.jobId).then((superadminRes) => {
                        this.setState({
                            loadingCompleted: false,
                            showReasonModal: false,
                            showManagerReviewModal: false,
                            showSuperadminReviewModal: false,
                            managerReviewReason: '',
                            reason: '',
                            requiresDtnReasonForReview: false,
                            requiresDtnReasonForSuperadmin: !!superadminRes.data.requiresWtnReason,
                            hasPendingSuperadminReview: true,
                            pendingStatus: {
                                status: 'completed',
                                waste_journey: statusObj.waste_journey ?? 0,
                            },
                        });

                        toastr.success(res.data?.message ?? 'Manager approved. Awaiting superadmin review.');
                        this.props.enableStatusButtons?.();

                        const isSuperadminReviewActive = sessionStorage.getItem('superadmin_review_active') === '1';

                        if (isSuperadminReviewActive) {
                            this.redirectToNextSuperadminJob();
                        } else {
                            this.props.refreshJob?.();
                        }
                    });

                    return;
                }

                this.setState({
                    loadingCompleted: false,
                    reason: '',
                    managerReviewReason: '',
                    pendingStatus: null,
                    requiresDtnReasonForReview: false,
                    requiresDtnReasonForSuperadmin: false,
                    hasPendingSuperadminReview: false,
                    showReasonModal: false,
                    showManagerReviewModal: false,
                    showSuperadminReviewModal: false,
                });

                toastr.success(res.data?.message ?? 'Job status successfully updated');

                this.props.enableStatusButtons?.();
                this.redirectToNextSuperadminJob();
            })
            .catch((err) => {
                console.error('❌ Error updating job status:', err);

                this.setState({ loadingCompleted: false });
                this.props.enableStatusButtons?.();

                toastr.error(err.response?.data?.message ?? 'There was a problem, please try again later');
            });
    };

    redirectToNextSuperadminJob = () => {
        const isSuperadminReviewActive = sessionStorage.getItem('superadmin_review_active') === '1';

        const storedQueue = sessionStorage.getItem('superadmin_review_queue');

        if (!isSuperadminReviewActive) {
            this.props.refreshJob?.();
            this.checkPendingSuperadminReview();
            return;
        }

        if (!storedQueue) {
            sessionStorage.removeItem('superadmin_review_queue');
            sessionStorage.removeItem('superadmin_review_active');

            window.location.assign('/jobs/superadmin-review');
            return;
        }

        try {
            const queue = JSON.parse(storedQueue);

            if (!queue.length) {
                sessionStorage.removeItem('superadmin_review_queue');
                sessionStorage.removeItem('superadmin_review_active');
                window.location.assign('/jobs/superadmin-review');
                return;
            }

            const nextJob = queue.shift();
            sessionStorage.setItem('superadmin_review_queue', JSON.stringify(queue));
            window.location.assign(nextJob);

            setTimeout(() => {
                window.location.replace(nextJob);
            }, 250);
        } catch (e) {
            sessionStorage.removeItem('superadmin_review_queue');
            sessionStorage.removeItem('superadmin_review_active');
            window.location.assign('/jobs/superadmin-review');
        }
    };
    renderYardageReasonAudit = () => {
        const review = this.props.quantityChangeReview;

        if (!review?.outcome) {
            return null;
        }

        const originalQuantity = review.quantity_before;
        const requestedQuantity = review.quantity_after;
        const managerApprovedQuantity = this.props.job?.provider_quantity;

        return (
            <div className="alert alert-light border mb-3">
                <strong>Quantity Change Review</strong>

                <small className="text-muted d-block mt-1">
                    Originally recorded as <strong>{originalQuantity} yard</strong>
                </small>

                <hr />

                <div className="mb-3">
                    <strong>Step 1: Requested quantity change</strong>

                    <div className="mt-1">
                        {originalQuantity} → {requestedQuantity} yard
                    </div>

                    <div className="mt-2">
                        <strong>Reason:</strong> {review.outcome}
                    </div>

                    <small className="text-muted d-block mt-2">
                        Requested / first review by: {review.requested_by || '—'}
                    </small>
                </div>

                <div className="mb-3">
                    <strong>Step 2: Manager review</strong>

                    <div className="mt-1">
                        {requestedQuantity} → {managerApprovedQuantity} yard
                    </div>

                    <small className="text-muted d-block mt-2">
                        Reviewed by: {review.created_by || '—'}
                        <br />
                        {review.created_at}
                    </small>
                </div>

                <hr />

                <div>
                    <strong>Step 3: Superadmin completion</strong>

                    <div className="mt-1">
                        This job will be completed as <strong>{managerApprovedQuantity} yard</strong>
                    </div>
                </div>
            </div>
        );
    };
    render() {
        const {
            loadingCompleted,
            showReasonModal,
            showManagerReviewModal,
            reason,
            managerReviewReason,
            requiresDtnReasonForReview,
            requiresDtnReasonForSuperadmin,
            hasPendingSuperadminReview,
        } = this.state;

        const roleName = this.props.user.role?.name;
        const isSuperadminUser = roleName === 'superadmin';

        const isCompleted = this.props.job.status === 'completed';

        const isAwaitingSuperadminReview = this.props.job.status === 'done' && hasPendingSuperadminReview;

        const hideCompletedButton = isCompleted || (isAwaitingSuperadminReview && !isSuperadminUser);

        return (
            <>
                {!hideCompletedButton && (
                    <button
                        type="button"
                        className="btn btn-cw-green ml-3"
                        style={{ width: 200, marginTop: 20, marginLeft: 15 }}
                        onClick={() =>
                            this.changeStatus({
                                status: 'completed',
                                waste_journey: 0,
                                manager_review_flag_reason: this.props.job.manager_review_flag_reason,
                            })
                        }
                        disabled={this.props.disabled}
                        title={this.props.title}
                    >
                        Mark as completed &nbsp;
                        {loadingCompleted && <span className="spinner-border spinner-border-sm" role="status" />}
                    </button>
                )}

                {showReasonModal && (
                    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Yardage Change Reason Required</h5>
                                </div>
                                <div className="modal-body">
                                    <textarea
                                        className="form-control"
                                        value={reason}
                                        onChange={(e) => this.setState({ reason: e.target.value })}
                                        placeholder="Enter Yardage Change reason"
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            this.setState({
                                                showReasonModal: false,
                                                reason: '',
                                                pendingStatus: null,
                                                requiresDtnReasonForReview: false,
                                                requiresDtnReasonForSuperadmin: false,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary"
                                        disabled={!reason.trim()}
                                        onClick={this.submitStatusWithReason}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showManagerReviewModal && (
                    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Manager Review Required</h5>
                                </div>

                                <div className="modal-body">
                                    <p>This job requires a manager review.</p>

                                    <label className="mt-2 font-weight-bold">Manager review reason</label>
                                    <textarea
                                        className="form-control mt-1"
                                        value={managerReviewReason}
                                        onChange={(e) => this.setState({ managerReviewReason: e.target.value })}
                                        placeholder="Enter manager review reason"
                                    />

                                    {requiresDtnReasonForReview && (
                                        <>
                                            <label className="mt-3 font-weight-bold">Yardage Change reason</label>
                                            <textarea
                                                className="form-control mt-1"
                                                value={reason}
                                                onChange={(e) => this.setState({ reason: e.target.value })}
                                                placeholder="Enter Yardage Change reason"
                                            />
                                        </>
                                    )}
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            this.setState({
                                                showManagerReviewModal: false,
                                                managerReviewReason: '',
                                                reason: '',
                                                pendingStatus: null,
                                                requiresDtnReasonForReview: false,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary"
                                        disabled={
                                            !managerReviewReason.trim() ||
                                            (requiresDtnReasonForReview && !reason.trim())
                                        }
                                        onClick={this.submitManagerReview}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {requiresDtnReasonForSuperadmin && (
                    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Superadmin Review Required</h5>
                                </div>

                                <div className="modal-body">
                                    {this.renderYardageReasonAudit()}
                                    <label className="mt-3 font-weight-bold">Yardage Change reason</label>
                                    <textarea
                                        className="form-control mt-1"
                                        value={reason}
                                        onChange={(e) => this.setState({ reason: e.target.value })}
                                        placeholder="Enter Yardage Change reason"
                                    />
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            this.setState({
                                                showSuperadminReviewModal: false,
                                                reason: '',
                                                pendingStatus: null,
                                                requiresDtnReasonForSuperadmin: false,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary"
                                        disabled={requiresDtnReasonForSuperadmin && !reason.trim()}
                                        onClick={this.submitSuperadminReview}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
